import type { PresentationTaskRunner } from "../../../runner/PresentationTaskRunner";
import type { GridLayer } from "../GridLayer";
import type { SymbolRemovalBurstSpawner } from "../../../effects/symbol-removal/SymbolRemovalBurstSpawner";
import type { SymbolRemovalEffect } from "../../../effects/symbol-removal/SymbolRemovalEffect";

export interface MaskRemovalDeps {
  grid: GridLayer;
  /** The mechanic's scope the removal runs under (slam / arm / abort target). */
  scope: string;
  /** Without a runner every masked cell releases at once (headless tests). */
  taskRunner: PresentationTaskRunner | null;
  /** The burst spawned on each removed cell; null → plain shrink. */
  burstSpawner: SymbolRemovalBurstSpawner | null;
  /** The per-symbol removal clip (the symbol's own Spine `out`); a cell it declines shrinks and bursts. */
  symbolEffect: SymbolRemovalEffect | null;
  /** The pool's release reason (its debug log). */
  releaseReason: string;
}

export interface MaskRemovalOptions {
  shrinkSeconds: number;
  columnStaggerSeconds: number;
  withinColumnStaggerSeconds: number;
}

/**
 * The masked (winning) cells leave the grid: a left-to-right, top-to-bottom wave per the stagger
 * options, each cell playing its symbol's removal clip or shrinking to nothing with a burst, then
 * released from the grid. Runner-driven (engine time), so it scales with the speed mode; the
 * subsequent collapse event drives the drop (`GravityDrop`). Shared by the tumble and the cascade.
 */
export class MaskRemoval {
  public constructor(private readonly deps: MaskRemovalDeps) {}

  public async removeByMask(mask: string, options: MaskRemovalOptions): Promise<void> {
    const { grid, scope, releaseReason } = this.deps;
    grid.assertMask(mask);

    // Removed indices by column, ascending row (top-to-bottom).
    const removalsByCol = new Map<number, number[]>();
    let minActiveCol = Infinity;
    for (let index = 0; index < mask.length; index++) {
      if (mask[index] !== "1") continue;
      const col = index % grid.gridColumnCount;
      if (col < minActiveCol) minActiveCol = col;
      let bucket = removalsByCol.get(col);
      if (!bucket) {
        bucket = [];
        removalsByCol.set(col, bucket);
      }
      bucket.push(index);
    }
    if (minActiveCol === Infinity) return;

    const runner = this.deps.taskRunner;
    if (!runner) {
      for (const indices of removalsByCol.values()) {
        for (const index of indices) {
          const view = grid.getSymbolViewAtIndex(index);
          if (!view) continue;
          this.deps.burstSpawner?.spawnAt(view.node);
          grid.releaseAt(index, releaseReason);
        }
      }
      return;
    }

    const run = async (): Promise<void> => {
      const ps: Promise<void>[] = [];
      for (const [col, indices] of removalsByCol.entries()) {
        indices.sort((a, b) => a - b);
        const colDelay = (col - minActiveCol) * options.columnStaggerSeconds;
        for (let i = 0; i < indices.length; i++) {
          const index = indices[i];
          const view = grid.getSymbolViewAtIndex(index);
          if (!view) continue;
          const node = view.node;
          const cellDelay = colDelay + i * options.withinColumnStaggerSeconds;
          ps.push(
            (async () => {
              if (cellDelay > 0) await runner.sleep(cellDelay, scope);
              // The symbol's own removal clip is the whole destruction (it ends with the symbol gone);
              // a declined cell shrinks with a burst.
              const animated = this.deps.symbolEffect?.play({ index, symbolId: view.getSymbolId() ?? "", cellNode: node, scope });
              if (animated) {
                await animated;
              } else {
                this.deps.burstSpawner?.spawnAt(node);
                await runner.tweenTo(node, options.shrinkSeconds, { scale: { x: 0, y: 0 } }, scope);
              }
              grid.releaseAt(index, releaseReason);
            })(),
          );
        }
      }
      await Promise.all(ps);
    };

    await runner.runInScope(scope, run);
  }
}
